/**
 * Template Types
 * Types for inspectieplan template management system
 */

import type { Database } from "./database.types";
import type { InspectieType } from "./shared/enums";
import { inspectieTypeLabels } from "./shared/inspection-metadata";

// Base Supabase type
type SupabaseTemplate = Database["public"]["Tables"]["templates"]["Row"];

export type EmailTemplateType = "email_offerte" | "email_rapport" | "email_certificaat" | "email_bundel";
export type InspectieplanTemplateType = "algemeen" | InspectieType;

// Template type enum - includes inspectieplan + communication templates
export type TemplateType = InspectieplanTemplateType | EmailTemplateType;

export const TEMPLATE_TYPE_LABELS: Record<TemplateType, string> = {
  algemeen: "Algemeen",
  ...inspectieTypeLabels,
  email_offerte: "Offerte e-mail",
  email_rapport: "Rapport e-mail",
  email_certificaat: "Certificaat e-mail",
  email_bundel: "Bundel e-mail",
};

export interface TemplateTextBlockV2 {
  id?: string;
  kind: "text";
  heading?: string;
  text: string;
}

export interface TemplateListBlockV2 {
  id?: string;
  kind: "list";
  heading?: string;
  items: string[];
}

/**
 * Normenblok: wordt bij het genereren van een inspectieplan gevuld uit de
 * normen-catalogus voor het inspectietype van de template ('algemeen': alle
 * inspectietypes van de offerte). Expandeert naar een gewone lijstsectie
 * ({ heading, items, normen }) — zie mapTemplateContentV2ToLegacy en
 * _template_content_v2_to_legacy_with_normen (SQL).
 */
export interface TemplateNormenBlockV2 {
  id?: string;
  kind: "normen";
  heading?: string;
}

export type TemplateBlockV2 = TemplateTextBlockV2 | TemplateListBlockV2 | TemplateNormenBlockV2;

export const DEFAULT_NORMEN_BLOCK_HEADING = "De volgende normen en voorschriften zijn van toepassing:";

// Canonical template content (stored in templates.content)
export interface TemplateContentV2 {
  version: 2;
  title: string;
  blocks: TemplateBlockV2[];
  metadata?: TemplateMetadata;
}

// JSONB column overrides — single source of truth used by both the Template entity
// and database.enhanced.ts (Supabase client type layer)
export type TemplateJsonbOverrides = { content: TemplateContentV2 };

// Enhanced template type with proper JSONB typing
export interface Template
  extends Omit<SupabaseTemplate, keyof TemplateJsonbOverrides | "type">, TemplateJsonbOverrides {
  type: TemplateType;
  // Extended properties for joins
  ibedrijf?: Database["public"]["Tables"]["ibedrijven"]["Row"];
}

// Legacy/print-ready content structure (stored in offertes.iplan)
export interface TemplateContent {
  title?: string;
  sections: TemplateSection[];
  metadata?: TemplateMetadata;
}

export interface TemplateSection {
  id?: string;
  heading?: string;
  content?: string;
  items?: string[];
  subsections?: TemplateSubsection[];
  /** Herkomst wanneer de sectie uit een normenblok is geëxpandeerd (catalogus-ids). */
  normen?: TemplateSectionNormRef[];
}

export interface TemplateSectionNormRef {
  id: string;
  naam: string;
  versie?: string | null;
}

export interface TemplateSubsection {
  heading?: string;
  content?: string;
  items?: string[];
}

export interface TemplateMetadata {
  version?: string;
  lastModified?: string;
  author?: string;
  description?: string;
}

// For template selection and management
export interface TemplateOption {
  id: string;
  naam: string;
  type: TemplateType;
  door_admin: boolean;
  versie: number;
  preview?: string; // First few characters of content for preview
}

// Combined template data for offerte iplan
export interface CombinedTemplateData {
  algemeen?: TemplateContent;
  inspections: {
    [key in InspectieType]?: TemplateContent;
  };
  metadata: {
    combinedAt: string;
    templateIds: string[];
    offerteId: string;
  };
}

// Template creation/editing forms
export interface TemplateFormData {
  naam: string;
  type: TemplateType;
  content: TemplateContentV2;
}

// Template management operations
export interface TemplateOperations {
  create: (data: TemplateFormData) => Promise<Template>;
  update: (id: string, data: Partial<TemplateFormData>) => Promise<Template>;
  delete: (id: string) => Promise<void>;
  duplicate: (id: string, newName: string) => Promise<Template>;
  getByType: (type: TemplateType, ibedrijfId?: string) => Promise<Template[]>;
  getForOfferte: (offerteId: string) => Promise<CombinedTemplateData>;
}

// Default template content structures
export const defaultTemplateContent: Record<InspectieplanTemplateType, TemplateContent> = {
  algemeen: {
    title: "Algemeen Inspectieplan",
    sections: [
      {
        heading: "Doel van de inspectie",
        content: "Deze inspectie wordt uitgevoerd conform de geldende normen en voorschriften.",
      },
      {
        heading: "Werkzaamheden",
        items: ["Visuele controle van de installatie", "Uitvoeren van metingen", "Opstellen van inspectie rapport"],
      },
    ],
  },
  scope8pi: {
    title: "Inspectieplan Scope 8 PI",
    sections: [
      {
        heading: "Scope 8 PI",
        content: "Periodieke inspectie van de elektrotechnische installatie.",
      },
      {
        heading: "Te controleren onderdelen",
        items: ["Hoofdaardingsleiding", "Aardingselektroden", "Equipotentiaalverbindingen", "Aardingsweerstanden"],
      },
    ],
  },
  scope8ebi: {
    title: "Inspectieplan Scope 8 EBI",
    sections: [
      {
        heading: "Scope 8 EBI",
        content: "Eerste beproeving van de elektrotechnische installatie conform NEN 3140.",
      },
    ],
  },
  scope10: {
    title: "Inspectieplan Scope 10",
    sections: [
      {
        heading: "Scope 10 - Brandveiligheid elektrische installatie",
        content: "Brandveiligheid elektrische installatie conform NTA 8220.",
      },
    ],
  },
  scope12pi: {
    title: "Inspectieplan Scope 12 PI",
    sections: [
      {
        heading: "Scope 12 PI",
        content: "Periodieke inspectie van de PV installatie conform NEN 3140.",
      },
    ],
  },
  scope12ebi: {
    title: "Inspectieplan Scope 12 EBI",
    sections: [
      {
        heading: "Scope 12 EBI",
        content: "Eerste beproeving van de PV installatie conform NEN 3140.",
      },
    ],
  },
  nen1010: {
    title: "Inspectieplan NEN 1010",
    sections: [
      {
        heading: "NEN 1010 Inspectie",
        content: "Inspectie conform NEN 1010 voorschriften.",
      },
    ],
  },
  nen3140: {
    title: "Inspectieplan NEN 3140",
    sections: [
      {
        heading: "NEN 3140 Inspectie",
        content: "Inspectie conform NEN 3140 voorschriften.",
      },
    ],
  },
};

export const defaultTemplateContentV2: Record<TemplateType, TemplateContentV2> = {
  algemeen: {
    version: 2,
    title: "Algemeen Inspectieplan",
    blocks: [
      {
        kind: "text",
        heading: "Doel van de inspectie",
        text: "Deze inspectie wordt uitgevoerd conform de geldende normen en voorschriften.",
      },
      {
        kind: "list",
        heading: "Werkzaamheden",
        items: ["Visuele controle van de installatie", "Uitvoeren van metingen", "Opstellen van inspectie rapport"],
      },
    ],
  },
  scope8pi: {
    version: 2,
    title: "Inspectieplan Scope 8 PI",
    blocks: [
      { kind: "normen", heading: DEFAULT_NORMEN_BLOCK_HEADING },
      {
        kind: "text",
        heading: "Scope 8 PI",
        text: "Periodieke inspectie van de elektrotechnische installatie conform NEN 3140.",
      },
      {
        kind: "list",
        heading: "Te controleren onderdelen",
        items: ["Hoofdaardingsleiding", "Aardingselektroden", "Equipotentiaalverbindingen", "Aardingsweerstanden"],
      },
    ],
  },
  scope8ebi: {
    version: 2,
    title: "Inspectieplan Scope 8 EBI",
    blocks: [
      { kind: "normen", heading: DEFAULT_NORMEN_BLOCK_HEADING },
      {
        kind: "text",
        heading: "Scope 8 EBI",
        text: "Eerste beproeving van de elektrotechnische installatie conform NEN 3140.",
      },
    ],
  },
  scope10: {
    version: 2,
    title: "Inspectieplan Scope 10",
    blocks: [
      { kind: "normen", heading: DEFAULT_NORMEN_BLOCK_HEADING },
      {
        kind: "text",
        heading: "Scope 10",
        text: "Brandveiligheid elektrische installatie conform NTA 8220.",
      },
    ],
  },
  scope12pi: {
    version: 2,
    title: "Inspectieplan Scope 12 PI",
    blocks: [
      { kind: "normen", heading: DEFAULT_NORMEN_BLOCK_HEADING },
      {
        kind: "text",
        heading: "Scope 12 PI",
        text: "Periodieke inspectie van de PV installatie.",
      },
    ],
  },
  scope12ebi: {
    version: 2,
    title: "Inspectieplan Scope 12 EBI",
    blocks: [
      { kind: "normen", heading: DEFAULT_NORMEN_BLOCK_HEADING },
      {
        kind: "text",
        heading: "Scope 12 EBI",
        text: "Eerste beproeving van de PV installatie.",
      },
    ],
  },
  nen1010: {
    version: 2,
    title: "Inspectieplan NEN 1010",
    blocks: [
      { kind: "normen", heading: DEFAULT_NORMEN_BLOCK_HEADING },
      {
        kind: "text",
        heading: "NEN 1010 Inspectie",
        text: "Inspectie conform NEN 1010 voorschriften.",
      },
    ],
  },
  nen3140: {
    version: 2,
    title: "Inspectieplan NEN 3140",
    blocks: [
      { kind: "normen", heading: DEFAULT_NORMEN_BLOCK_HEADING },
      {
        kind: "text",
        heading: "NEN 3140 Inspectie",
        text: "Inspectie conform NEN 3140 voorschriften.",
      },
    ],
  },
  email_offerte: {
    version: 2,
    title: "Offerte {{offerte.offerteNr}} - Inspeqt",
    blocks: [
      {
        kind: "text",
        text: [
          "Beste {{opdrachtgever.contactnaam}},",
          "",
          "Hierbij ontvangt u de offerte voor {{locatie.naam}}.",
          "Het totaalbedrag bedraagt {{offerte.totaal}} exclusief btw.",
          "",
          "U kunt de offerte via de link in deze e-mail direct bekijken en ondertekenen.",
          "",
          "Heeft u vragen? Reageer dan gerust op deze e-mail.",
          "",
          "Met vriendelijke groet,",
          "{{sender.name}}",
        ].join("\n"),
      },
    ],
    metadata: {
      description:
        "Gebruik placeholders zoals {{opdrachtgever.contactnaam}}, {{locatie.naam}}, {{offerte.offerteNr}}, {{offerte.totaal}} en {{sender.name}}.",
    },
  },
  email_rapport: {
    version: 2,
    title: "Rapport {{rapport.inspectieRapportNr}} - {{sender.name}}",
    blocks: [
      {
        kind: "text",
        text: [
          "Beste {{opdrachtgever.contactnaam}},",
          "",
          "Hierbij ontvangt u het rapport voor {{locatie.naam}}.",
          "Het rapportnummer is {{rapport.inspectieRapportNr}}.",
          "Inspectietypes: {{rapport.inspectieTypes}}.",
          "",
          "U kunt het rapport via de link in deze e-mail direct openen.",
          "",
          "Heeft u vragen? Reageer dan gerust op deze e-mail.",
          "",
          "Met vriendelijke groet,",
          "{{sender.name}}",
        ].join("\n"),
      },
    ],
    metadata: {
      description:
        "Gebruik placeholders zoals {{opdrachtgever.contactnaam}}, {{opdrachtgever.naam}}, {{locatie.naam}}, {{rapport.inspectieRapportNr}}, {{rapport.inspectieTypes}} en {{sender.name}}.",
    },
  },
  email_certificaat: {
    version: 2,
    title: "Certificaat {{certificaat.type}} - {{rapport.inspectieRapportNr}}",
    blocks: [
      {
        kind: "text",
        text: [
          "Beste {{opdrachtgever.contactnaam}},",
          "",
          "Hierbij ontvangt u het {{certificaat.type}}-certificaat voor {{locatie.naam}}.",
          "Het bijbehorende rapportnummer is {{rapport.inspectieRapportNr}}.",
          "",
          "U kunt het certificaat via de link in deze e-mail direct downloaden.",
          "",
          "Heeft u vragen? Reageer dan gerust op deze e-mail.",
          "",
          "Met vriendelijke groet,",
          "{{sender.name}}",
        ].join("\n"),
      },
    ],
    metadata: {
      description:
        "Gebruik placeholders zoals {{opdrachtgever.contactnaam}}, {{opdrachtgever.naam}}, {{locatie.naam}}, {{rapport.inspectieRapportNr}}, {{certificaat.type}} en {{sender.name}}.",
    },
  },
  email_bundel: {
    version: 2,
    title: "Offertebundel {{bundel.bundelNr}} - {{sender.name}}",
    blocks: [
      {
        kind: "text",
        text: [
          "Beste {{opdrachtgever.contactnaam}},",
          "",
          "Hierbij ontvangt u de offertebundel {{bundel.bundelNr}}.",
          "Het totaalbedrag bedraagt {{bundel.totaal}} exclusief btw.",
          "",
          "U kunt de bundel via de link in deze e-mail direct bekijken en ondertekenen.",
          "",
          "Heeft u vragen? Reageer dan gerust op deze e-mail.",
          "",
          "Met vriendelijke groet,",
          "{{sender.name}}",
        ].join("\n"),
      },
    ],
    metadata: {
      description:
        "Gebruik placeholders zoals {{opdrachtgever.contactnaam}}, {{opdrachtgever.naam}}, {{bundel.bundelNr}}, {{bundel.totaal}} en {{sender.name}}.",
    },
  },
};
